import React, { useMemo, useRef, useEffect } from 'react';
import { Box, Grid, Typography, Button, Link } from '@mui/material';
import ChartSectionEditor from './ChartSectionEditor';
import MarkdownRenderer from '@/components/shared/MarkdownRenderer';
import type {
  ChartSection,
  ChartElement,
  ChartWaypoint,
  ElementsBySection,
} from '@/types';

interface ChartContentsEditorProps {
  sections: ChartSection[];
  elementsBySection: ElementsBySection;
  onSectionsChange: (sections: ChartSection[]) => void;
  onElementsChange: (sectionId: string, elements: ChartElement[]) => void;
  isEditable: boolean;
  editMode?: 'full' | 'prompt-only';
  waypoints: ChartWaypoint[];
  headerRef: React.RefObject<HTMLDivElement>;
}

function ChartContentsEditor({
  sections,
  elementsBySection,
  onSectionsChange,
  onElementsChange,
  isEditable,
  editMode = 'full',
  waypoints,
  headerRef,
}: ChartContentsEditorProps): React.JSX.Element {
  // Generate markdown preview for a single section
  const generateSectionMarkdown = (
    section: ChartSection,
    elements: ChartElement[],
  ): string => {
    // Special handling for title sections
    if (section.style === 'title') {
      return '# Document Title\n\n';
    }

    const headingPrefix = section.number ? `${section.number}. ` : '';
    const headingText = section.heading || 'Untitled Section';

    // Map style to markdown heading
    const headingLevel =
      section.style === 'h1'
        ? '#'
        : section.style === 'h2'
          ? '##'
          : section.style === 'h3'
            ? '###'
            : section.style === 'h4'
              ? '####'
              : section.style === 'h5'
                ? '#####'
                : '######';
    let markdown = `${headingLevel} ${headingPrefix}${headingText}\n\n`;

    const sortedElements = (elements || []).sort(
      (a, b) => (a.order || 0) - (b.order || 0),
    );

    sortedElements.forEach((element) => {
      if (element.type === 'title') {
        markdown += `## ${element.description || ''}\n\n`;
      } else if (element.type === 'narrative') {
        markdown += `${element.description || ''}\n\n`;
      } else if (element.type === 'table' && element.metadata) {
        // For tables, show description before the table
        if (element.description) {
          markdown += `${element.description}\n\n`;
        }
        // Generate table markdown
        const headers = (element.metadata['headers'] as string[]) || [];
        if (headers.length > 0) {
          markdown += `**${(element.metadata['title'] as string) || 'Table'}**\n\n`;
          markdown += `| ${headers.join(' | ')} |\n`;
          markdown += `| ${headers.map(() => '---').join(' | ')} |\n`;
          markdown += `| ${headers.map(() => '').join(' | ')} |\n\n`;
        }
      } else if (element.type === 'static') {
        markdown += `${element.description || ''}\n\n`;
      }
    });

    return markdown;
  };

  const sortedSections = useMemo(() => {
    return [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [sections]);

  const lastSectionRef = useRef<HTMLDivElement>(null);
  const previousSectionsLengthRef = useRef<number>(sections.length);

  // Scroll to bottom when a new section is added
  useEffect(() => {
    if (sections.length > previousSectionsLengthRef.current) {
      // A new section was added
      setTimeout(() => {
        if (lastSectionRef.current) {
          lastSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
    previousSectionsLengthRef.current = sections.length;
  }, [sections.length]);

  const handleScrollToTop = (): void => {
    if (headerRef && headerRef.current) {
      headerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <Grid container spacing={2}>
      {/* Header row */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            Edit Chart Contents
          </Typography>
          {isEditable && editMode === 'full' && (
            <Button
              variant='contained'
              size='small'
              onClick={() => {
                const newSection: ChartSection = {
                  heading: 'New Section',
                  style: 'h1',
                  number: null,
                  order: sections.length,
                };
                onSectionsChange([...sections, newSection]);
              }}
            >
              + Add Section
            </Button>
          )}
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 'bold',
            p: 2,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}
        >
          Preview
        </Typography>
      </Grid>

      {/* Sections */}
      {sortedSections.length === 0 ? (
        <>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                No sections yet. Use the buttons above to add a section.
              </Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                No preview available.
              </Typography>
            </Box>
          </Grid>
        </>
      ) : (
        sortedSections.map((section, index) => {
          // Handle both entity_id and temporary IDs for new sections
          const sectionKey = section.entity_id || `temp-${section.order}`;
          const sectionElements = (elementsBySection[sectionKey] || []).map(
            (el) => ({
              ...el,
              section_id: section.entity_id || null,
            }),
          );
          const sectionMarkdown = generateSectionMarkdown(
            section,
            sectionElements,
          );

          const isLastSection = index === sortedSections.length - 1;

          return (
            <React.Fragment key={section.entity_id || `section-${index}`}>
              {/* Left side - Form Editor */}
              <Grid
                size={{ xs: 12, md: 6 }}
                ref={isLastSection ? lastSectionRef : null}
              >
                <ChartSectionEditor
                  section={section}
                  elements={sectionElements}
                  onSectionChange={(updatedSection) => {
                    const newSections = [...sections];
                    const sectionIndex = newSections.findIndex(
                      (s) =>
                        (s.entity_id &&
                          section.entity_id &&
                          s.entity_id === section.entity_id) ||
                        (!s.entity_id && !section.entity_id && s === section),
                    );
                    if (sectionIndex >= 0) {
                      newSections[sectionIndex] = {
                        ...newSections[sectionIndex],
                        ...updatedSection,
                      };
                      onSectionsChange(newSections);
                    }
                  }}
                  onMoveUp={() => {
                    const sortedSections = [...sections].sort(
                      (a, b) => (a.order || 0) - (b.order || 0),
                    );
                    const sectionIndex = sortedSections.findIndex(
                      (s) =>
                        (s.entity_id &&
                          section.entity_id &&
                          s.entity_id === section.entity_id) ||
                        (!s.entity_id && !section.entity_id && s === section),
                    );
                    if (sectionIndex > 0) {
                      // Swap the sections
                      [
                        sortedSections[sectionIndex],
                        sortedSections[sectionIndex - 1],
                      ] = [
                        sortedSections[sectionIndex - 1],
                        sortedSections[sectionIndex],
                      ];
                      // Recalculate all order values to be sequential
                      sortedSections.forEach((s, idx) => {
                        s.order = idx;
                      });
                      onSectionsChange(sortedSections);
                    }
                  }}
                  onMoveDown={() => {
                    const sortedSections = [...sections].sort(
                      (a, b) => (a.order || 0) - (b.order || 0),
                    );
                    const sectionIndex = sortedSections.findIndex(
                      (s) =>
                        (s.entity_id &&
                          section.entity_id &&
                          s.entity_id === section.entity_id) ||
                        (!s.entity_id && !section.entity_id && s === section),
                    );
                    if (sectionIndex < sortedSections.length - 1) {
                      // Swap the sections
                      [
                        sortedSections[sectionIndex],
                        sortedSections[sectionIndex + 1],
                      ] = [
                        sortedSections[sectionIndex + 1],
                        sortedSections[sectionIndex],
                      ];
                      // Recalculate all order values to be sequential
                      sortedSections.forEach((s, idx) => {
                        s.order = idx;
                      });
                      onSectionsChange(sortedSections);
                    }
                  }}
                  isFirst={index === 0}
                  isLast={index === sortedSections.length - 1}
                  onElementsChange={(newElements) => {
                    // Ensure all elements have the correct section_id
                    const elementsWithSectionId = newElements.map((el) => ({
                      ...el,
                      section_id: section.entity_id || null,
                    }));
                    // Use the same sectionKey logic for consistency
                    const sectionKey =
                      section.entity_id || `temp-${section.order}`;
                    onElementsChange(sectionKey, elementsWithSectionId);
                  }}
                  onDeleteSection={() => {
                    const newSections = sections.filter((s) => {
                      if (section.entity_id && s.entity_id) {
                        return s.entity_id !== section.entity_id;
                      }
                      return s !== section;
                    });
                    // Also remove elements for this section
                    const sectionKey =
                      section.entity_id || `temp-${section.order}`;
                    const newElements = { ...elementsBySection };
                    delete newElements[sectionKey];
                    onElementsChange(sectionKey, []);
                    onSectionsChange(newSections);
                  }}
                  onAddSection={() => {
                    const newSection: ChartSection = {
                      heading: 'New Section',
                      style: 'h1',
                      number: null,
                      order: sections.length,
                    };
                    onSectionsChange([...sections, newSection]);
                  }}
                  isEditable={isEditable}
                  editMode={editMode}
                  waypoints={waypoints}
                />
              </Grid>

              {/* Right side - Preview */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ p: 2 }}>
                  <MarkdownRenderer content={sectionMarkdown} />
                </Box>
              </Grid>
            </React.Fragment>
          );
        })
      )}

      {/* Back to top link */}
      <Grid size={{ xs: 12 }}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Link
            component='button'
            variant='body2'
            onClick={handleScrollToTop}
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Back to top
          </Link>
        </Box>
      </Grid>
    </Grid>
  );
}

export default ChartContentsEditor;
